import {endOfDay, isAfter, isBefore, isSameDay, startOfDay} from 'date-fns';
import {useComponentsTheme} from 'platform/components';
import {Box, getSize, getValueByDevice, Grid, GridItem, Show, useDevice} from 'platform/foundation';

import {prop, sortBy} from 'ramda';

import {RequiredTestIdProps, suffixTestId, useOnMount} from 'shared/web';

import {useActiveId} from '../hooks/useActiveId';
import {useDnd} from '../hooks/useDnd';
import {useIsDragging} from '../hooks/useIsDragging';
import {BigCalendarEvent} from '../types/BigCalendarEvent';
import {calculateTimePosition} from '../utils/calculateTimePosition';
import {getAllDayHeaderElementId} from '../utils/getAllDayHeaderElementId';
import {getEventsForDate} from '../utils/getEventsForDate';
import {getTimeSlotElementId} from '../utils/getTimeSlotElementId';
import {scrollToCalendarStart} from '../utils/scrollToCalendarStart';
import {AllDayEventItem} from './AllDayEventItem';
import {CurrentTimeIndicator} from './CurrentTimeIndicator';
import {DroppableCell} from './DroppableCell';
import {EventItem} from './EventItem';
import {TimeSlot} from './TimeSlot';

interface DayViewProps extends RequiredTestIdProps {
  currentDate: Date;
  events: BigCalendarEvent[];
  isReadOnly?: boolean;
  startHourView?: number;
  onEventClick?: (event: BigCalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  enableHover?: boolean;
  onEventMouseEnter?: (event: BigCalendarEvent, mouseEvent: React.MouseEvent) => void;
  onEventMouseLeave?: (event: BigCalendarEvent) => void;
}

export function DayView(props: DayViewProps) {
  const theme = useComponentsTheme();
  const device = useDevice();

  const isDragging = useIsDragging();
  const activeId = useActiveId();
  const {activeDnDId} = useDnd();

  const draggedEvent = props.events.find((e) => e.id === activeId || e.id === activeDnDId);

  const {allDayEvents, regularEvents} = getEventsForDate(props.events, props.currentDate);

  // The dragged event is drawn by the drag overlay, so its own chip is hidden while it moves.
  const allDayChips = sortByStart(allDayEvents).filter(
    (event) => !(isDragging && event.id === activeId)
  );
  const hasAllDayEvents = allDayChips.length > 0;

  const dayStart = startOfDay(props.currentDate);
  const dayEnd = endOfDay(props.currentDate);

  useOnMount(() => {
    scrollToCalendarStart(props['data-testid'], props.startHourView);
  });

  return (
    <Box position="relative" width="100%" height="100%">
      <div
        data-testid={suffixTestId('dayView-allDayEvents', props)}
        id={getAllDayHeaderElementId(props['data-testid'])}
        className="sticky overflow-hidden bg-[var(--color-palettes-neutral-10-100)]"
        style={{
          top: getSize(getValueByDevice(device, 10, 15)),
          zIndex: theme.zIndices.BIG_CALENDAR_STICKY_ALLDAY_SLOT,
        }}
      >
        <Grid columns={20} spacing={0}>
          <GridItem span={[3, 1]}>
            <div
              className="h-full bg-[var(--color-palettes-neutral-10-100)]"
              style={{
                borderBottom: hasAllDayEvents
                  ? `1px solid ${theme.colors.general.separator}`
                  : 'none',
              }}
            />
          </GridItem>

          <GridItem span={[17, 19]}>
            <div
              className="flex flex-wrap"
              style={{
                gap: getSize(1),
                minWidth: getSize(25),
                maxWidth: getValueByDevice(device, getSize(25), undefined),
                padding: hasAllDayEvents ? `${getSize(1)} ${getSize(2)}` : 0,
                borderLeft: hasAllDayEvents
                  ? `1px solid ${theme.colors.general.separator}`
                  : 'none',
                borderBottom: hasAllDayEvents
                  ? `1px solid ${theme.colors.general.separator}`
                  : 'none',
              }}
            >
              {allDayChips.map((event, eventIndex) => (
                <div
                  key={event.id}
                  className="min-w-0"
                  style={{maxWidth: `calc(50% - ${getSize(1)} / 2)`}}
                >
                  <AllDayEventItem
                    data-testid={suffixTestId(`dayView-[${eventIndex}]`, props)}
                    event={event}
                    isCompact
                    isStartClipped={isBefore(event.start, dayStart)}
                    isEndClipped={isAfter(event.end, dayEnd)}
                    onClick={props.onEventClick}
                    enableHover={props.enableHover}
                    onMouseEnter={props.onEventMouseEnter}
                    onMouseLeave={props.onEventMouseLeave}
                  />
                </div>
              ))}
            </div>
          </GridItem>
        </Grid>
      </div>

      <Box overflowX="hidden" overflowY={isDragging ? 'hidden' : 'auto'}>
        <Grid data-testid={props['data-testid']} columns={20} spacing={0}>
          <GridItem span={[3, 1]}>
            {TIME_SLOTS.map((hour) => (
              <TimeSlot
                key={hour}
                hour={hour}
                data-testid={suffixTestId(`dayView-[${hour}]`, props)}
                id={getTimeSlotElementId(props['data-testid'], hour)}
              />
            ))}
          </GridItem>

          <GridItem span={[17, 19]}>
            <Box position="relative">
              {TIME_SLOTS.map((hour) => (
                <DroppableCell
                  key={`droppableCell-${hour}`}
                  data-testid={suffixTestId(`dayView-[${hour}]`, props)}
                  date={props.currentDate}
                  hour={hour}
                  draggedEvent={draggedEvent}
                  onDateClick={props.onDateClick}
                />
              ))}

              <div
                className="pointer-events-none absolute top-0"
                style={{right: '3px', left: '4px'}}
              >
                <Show when={isSameDay(props.currentDate, new Date())}>
                  <CurrentTimeIndicator data-testid={suffixTestId('dayView', props)} />
                </Show>

                {regularEvents.map((event, eventIndex) => {
                  if (isDragging && event.id === activeId) {
                    return null;
                  }

                  return (
                    <EventItem
                      key={event.id}
                      data-testid={suffixTestId(`dayView-[${eventIndex}]`, props)}
                      isReadOnly={props.isReadOnly}
                      event={event}
                      top={calculateTimePosition(event.start)}
                      onClick={props.onEventClick}
                      enableHover={props.enableHover}
                      onMouseEnter={props.onEventMouseEnter}
                      onMouseLeave={props.onEventMouseLeave}
                    />
                  );
                })}
              </div>
            </Box>
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );
}
const NUMBER_OF_HOURS = 24;
const TIME_SLOTS = Array.from({length: NUMBER_OF_HOURS}, (_, hour) => hour);

/** Chips wrap in reading order, so earlier-starting events come first. */
const sortByStart = sortBy<BigCalendarEvent>(prop('start'));
